import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "../../../components/Navbar/Navbar";
import { Footer } from "../../../components/Footer/Footer";
import { Banner } from "../../../components/Banner/Banner";
import proImage from "../../../assets/images/Prodigy.png"; // 기본 프로필 이미지
import PLImage from "../../../assets/images/PolygonL.svg";
import PRImage from "../../../assets/images/PolygonR.svg";
import Check from "../../../assets/images/check.svg";
import "../../board/gallery/gallery.css";
import "./gallery.css";

export const Gallery = () => {
  const BOARD_URL = process.env.REACT_APP_BOARD_API_URL || 'http://localhost:5001';
  
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [items, setItems] = useState([]); // 서버에서 가져온 갤러리 데이터
  const [selected, setSelected] = useState({}); // 체크된 항목들
  const [loading, setLoading] = useState(true);
  
  // 페이지네이션 & 검색 상태
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6; // 한 페이지에 보여줄 사진 수
  const [searchTerm, setSearchTerm] = useState(""); // 검색어

  // 1. 초기 데이터 로드 및 로그인 체크
  useEffect(() => {
    const token = localStorage.getItem('token'); 
    if (token) setIsLoggedIn(true);

    const fetchGallery = async () => {
      try {
        const res = await fetch(`${BOARD_URL}/gallery`);
        if (!res.ok) throw new Error("데이터 로드 실패");
        const data = await res.json();
        setItems(data); // DB 데이터 저장
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, [BOARD_URL]);

  // 2. 검색 필터링
  const filteredItems = items.filter(item => 
    item.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // 3. 페이지네이션 계산
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  // 체크박스 관련 로직 (몽고DB는 _id를 사용함에 주의!)
  const allIds = useMemo(() => currentItems.map(i => i._id), [currentItems]);
  const selectedIds = useMemo(() => Object.keys(selected).filter(id => selected[id]), [selected]);
  
  // 현재 페이지의 항목들이 모두 선택되었는지 확인
  const allChecked = allIds.length > 0 && allIds.every(id => selected[id]);
  const anyChecked = selectedIds.length > 0;

  const toggleOne = (id, on) => {
    setSelected(prev => {
      const next = { ...prev };
      if (on) next[id] = true;
      else delete next[id];
      return next;
    });
  };

  const toggleAll = (on) => {
    const next = { ...selected };
    if (on) {
      allIds.forEach(id => (next[id] = true));
    } else {
      allIds.forEach(id => delete next[id]);
    }
    setSelected(next);
  };

  // 4. 선택 삭제 기능 (서버 연동)
  const handleBulkDelete = async () => {
    if (!anyChecked) return;
    if (!window.confirm(`${selectedIds.length}개 항목을 삭제하시겠습니까?`)) return;

    const token = localStorage.getItem('token');
    
    try {
      // 선택된 모든 ID에 대해 삭제 요청을 병렬로 보냄
      await Promise.all(selectedIds.map(id => 
        fetch(`${BOARD_URL}/gallery/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ));

      alert("삭제되었습니다.");
      
      // 화면 갱신: 삭제된 항목 제외하고 다시 세팅
      setItems(prev => prev.filter(item => !selectedIds.includes(item._id)));
      setSelected({}); // 선택 초기화
    } catch (err) {
      console.error(err);
      alert("삭제 중 오류가 발생했습니다.");
    }
  };

  // 페이지 변경 핸들러
  const handlePageChange = (pageNum) => {
    setCurrentPage(pageNum);
    setSelected({}); // 페이지 넘기면 선택 초기화
  };

  return (
    <>
      <Navbar />
      <Banner title="갤러리" />

      <div className="page-gallery">
        
        {/* 관리자 툴바 */}
        {isLoggedIn && (
          <div className="admin-toolbar">
            <label className="admin-select-all">
              <input
                type="checkbox"
                checked={allChecked}
                onChange={(e) => toggleAll(e.target.checked)}
              />
              전체 선택
            </label>

            <button className="admin-btn secondary" disabled={!anyChecked} onClick={handleBulkDelete}>
              선택삭제
            </button>

            <Link to="/gallery/GalleryWrite" className="admin-btn primary">글쓰기</Link>
          </div>
        )}

        {/* 갤러리 리스트 영역 */}
        <div className="gallery-main">
          {loading ? (
             <div style={{textAlign:'center', padding:'50px'}}>로딩중...</div>
          ) : (
            <div className="gallery-list">
              {currentItems.length === 0 ? (
                <div style={{width:'100%', textAlign:'center', padding:'50px'}}>등록된 게시물이 없습니다.</div>
              ) : (
                currentItems.map(item => (
                  <div className="gallery-list-box" key={item._id}>
                    <div className="gallery-list-info">
                      
                      {/* 썸네일 영역 */}
                      <Link to={`/gallery/Gallerydetail/${item._id}`} className="gallery-list-picture">
                        {/* 관리자용 체크박스 */}
                        {isLoggedIn && (
                          <label className="card-checkbox" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={!!selected[item._id]}
                              onChange={(e) => toggleOne(item._id, e.target.checked)}
                            />
                            {selected[item._id] && <img src={Check} alt="check" />}
                          </label>
                        )}
                        
                        {/* 이미지: 서버 주소 + 파일 경로 조합 */}
                        <img 
                            src={item.imageUrl}
                            alt={item.title} 
                            style={{ objectFit: 'cover' }}
                        />
                      </Link>
                      
                      {/* 상세 정보 및 링크 */}
                      <Link to={`/gallery/Gallerydetail/${item._id}`} className="gallery-list-info-link">
                        <div className="gallery-list-content">
                          <div className="gallery-list-pro">
                            <img src={proImage} alt="작성자" />
                          </div>
                          <div className="gallery-list-text">
                            <div className="gallery-list-title">{item.title}</div>
                            <div className="gallery-list-date">
                                관리자 · {new Date(item.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                      </Link>   
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* 페이지 네비게이션 (Notice 스타일 적용) */}
  <div className="gallery-movebar">
    {/* 이전 페이지 화살표 */}
    <img 
      src={PLImage} 
      alt="이전" 
      className="arrow" 
      onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
      style={{ 
        cursor: currentPage > 1 ? 'pointer' : 'default', 
        opacity: currentPage > 1 ? 1 : 0.3 
      }}
    />

    <div className="pages">
      {Array.from({ length: totalPages }, (_, i) => i + 1).map(num => (
        <span 
          key={num} 
          className={`page-num ${currentPage === num ? 'active' : ''}`}
          onClick={() => handlePageChange(num)}
        >
          {num}
        </span>
      ))}
    </div>

    {/* 다음 페이지 화살표 */}
    <img 
      src={PRImage} 
      alt="다음" 
      className="arrow" 
      onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
      style={{ 
        cursor: currentPage < totalPages ? 'pointer' : 'default', 
        opacity: currentPage < totalPages ? 1 : 0.3 
      }}
    />
  </div>

{/* ... 아래쪽 Footer 생략 ... */}
        
      </div>

      <Footer />
    </>
  );
}